// ── recentFillTape — the combined Kalshi + Polymarket display tape ──
//
// Display-only merge for the Recent Fills rail and the /fills popup (#457).
// Both venues keep their own read path: Kalshi arrives through accountStore's
// ingestFillBatch/processFill (which own dedup, ordering and sounds), Polymarket
// through the authenticated relay history route. This module never touches either —
// it takes already-loaded arrays and answers one question: what are the
// most recent N fills, newest first, across all venues?
//
// Deliberately free of Zustand, React, query clients, sound helpers, the
// current time and any side effect, so the ordering and identity rules below
// are testable as plain data. Poly rows must never enter processFill: that
// path owns Kalshi audio, and #457 adds no Polymarket sounds.
//
// #1007 adds Polymarket US as a third, equally display-only venue: its rows
// never enter processFill either (international fills raise no alert, so US
// parity needs none), and its amounts stay exact decimal strings.

import type { KalshiFill } from '@/types/kalshi';
import type { PolyRelayFill } from '@/lib/polymarket/relay';
import type { UsFill, UsFillDisputedField } from '@/lib/polymarketUs/fillsClient';

/** One bound for the whole tape, shared with accountStore's Kalshi slice and
 *  with the Poly request limit. Merge-then-slice is only correct while each
 *  venue supplies at least this many candidates, so all three must move
 *  together — that is why this constant is imported rather than repeated. */
export const RECENT_FILL_LIMIT = 20;

export type KalshiOrPolyTapeRow = {
  /** Venue-qualified display identity. Kalshi fill ids and Poly ledger ids are
   *  issued by different systems, so an unqualified id could collide across
   *  venues and silently drop one of the two fills. */
  key: `kalshi:${string}` | `polymarket:${string}`;
  venue: 'kalshi' | 'polymarket';
  /** The venue's own fill id, for display/debugging only. Null on legacy poly
   *  rows, which is exactly why it cannot be the key. */
  sourceFillId: string | null;
  ticker: string;
  /** Relay-persisted poly market identity. Null for Kalshi, whose label is
   *  derived from the ticker by describeMarket at render time. */
  marketLabel: string | null;
  marketSport: string | null;
  side: 'yes' | 'no';
  action: 'buy' | 'sell';
  count: number;
  priceCents: number;
  executedAt: string;
  executedAtMs: number;
};

/** #1007. US execution ids are unique per account, not globally, so the key is
 *  account-qualified. Amounts are the relay's exact decimal strings (fractional
 *  sizes, sub-cent prices) and are never converted to numbers. `price` is null
 *  when the basis is unverified, missing or disputed; a NO fill's price is
 *  1 − yes, marked provisional (#1015). `executedAt` is the venue time and may
 *  be null; `observedAtMs` is G1's receipt time, kept apart so it is never
 *  shown as the fill time. A `conflicting` row (#1015) is shown with each
 *  disputed field null and named in `disputed`; the relay sends one row per
 *  key, so the first-wins merge below never chooses between US rows. */
export type UsTapeRow = {
  key: `polymarket_us:${string}:${string}`;
  venue: 'polymarket_us';
  accountId: string;
  sourceFillId: string;
  marketSlug: string | null;
  marketTitle: string | null;
  side: 'yes' | 'no' | null;
  action: 'buy' | 'sell' | null;
  quantity: string | null;
  price: string | null;
  priceBasis: UsFill['priceBasis'];
  executedAt: string | null;
  executedAtMs: number;
  observedAtMs: number;
  evidenceState: UsFill['evidenceState'];
  disputed: UsFillDisputedField[];
};

export type RecentFillTapeRow = KalshiOrPolyTapeRow | UsTapeRow;

/** Unparseable times sort to the tail rather than being dropped or read as
 *  "now". A row the tape cannot place is still a fill the trader made, and
 *  defaulting it to the current instant would float it above real activity.
 *  Neither venue is expected to reach this today (the Task 3 decoder rejects a
 *  bad poly executed_at, and accountStore coalesces a missing Kalshi
 *  created_at), so this is a module invariant, not a live case. */
function toMillis(iso: string): number {
  const ms = Date.parse(iso);
  return Number.isFinite(ms) ? ms : Number.NEGATIVE_INFINITY;
}

function fromKalshi(fill: KalshiFill): KalshiOrPolyTapeRow {
  return {
    key: `kalshi:${fill.fillId}`,
    venue: 'kalshi',
    sourceFillId: fill.fillId,
    ticker: fill.ticker,
    marketLabel: null,
    marketSport: null,
    side: fill.side,
    action: fill.action,
    count: fill.count,
    priceCents: fill.price,
    executedAt: fill.createdAt,
    executedAtMs: toMillis(fill.createdAt),
  };
}

function fromPoly(fill: PolyRelayFill): KalshiOrPolyTapeRow {
  return {
    key: `polymarket:${fill.ledger_id}`,
    venue: 'polymarket',
    sourceFillId: fill.fill_id,
    ticker: fill.ticker,
    marketLabel: fill.market_label,
    marketSport: fill.market_sport,
    side: fill.side,
    action: fill.action,
    // Counts and cent prices pass through untouched: poly sizes are fractional
    // and its prices are sub-cent, so rounding here would misreport the fill.
    count: fill.count,
    priceCents: fill.price_cents,
    executedAt: fill.executed_at,
    executedAtMs: toMillis(fill.executed_at),
  };
}

/** Exact sort key for a normalized venue time: text order alone is wrong
 *  ('…:00.5Z' < '…:00Z'), so the fraction is padded to nanoseconds. */
function exactTimeKey(iso: string | null): string {
  const m = iso === null ? null : /^(.{19})(?:\.(\d{1,9}))?Z$/.exec(iso);
  return m ? `${m[1]}.${(m[2] ?? '').padEnd(9, '0')}` : '';
}

function fromUs(fill: UsFill): UsTapeRow {
  return {
    key: `polymarket_us:${fill.accountId}:${fill.executionId}`,
    venue: 'polymarket_us',
    accountId: fill.accountId,
    sourceFillId: fill.executionId,
    marketSlug: fill.marketSlug,
    marketTitle: fill.marketTitle,
    side: fill.outcome,
    action: fill.action,
    quantity: fill.quantity,
    price: fill.price,
    priceBasis: fill.priceBasis,
    executedAt: fill.executedAt,
    // No venue time: the fill still shows, at the tail (never read as "now").
    executedAtMs: fill.executedAt === null ? Number.NEGATIVE_INFINITY : toMillis(fill.executedAt),
    observedAtMs: fill.observedAtMs,
    evidenceState: fill.evidenceState,
    disputed: fill.disputed,
  };
}

/**
 * Merge all venues into one bounded, newest-first tape.
 *
 * The limit applies to the UNION, after sorting: taking N per venue first
 * would drop rows that belong in the combined top N whenever one venue is
 * busier than the other.
 */
export function buildRecentFillTape(
  kalshiFills: readonly KalshiFill[],
  polyFills: readonly PolyRelayFill[],
  limit: number = RECENT_FILL_LIMIT,
  usFills: readonly UsFill[] = [],
): RecentFillTapeRow[] {
  const byKey = new Map<string, RecentFillTapeRow>();
  for (const fill of kalshiFills) {
    const row = fromKalshi(fill);
    if (!byKey.has(row.key)) byKey.set(row.key, row);
  }
  for (const fill of polyFills) {
    const row = fromPoly(fill);
    if (!byKey.has(row.key)) byKey.set(row.key, row);
  }
  for (const fill of usFills) {
    const row = fromUs(fill);
    if (!byKey.has(row.key)) byKey.set(row.key, row);
  }

  return [...byKey.values()]
    .sort((a, b) => {
      if (a.executedAtMs !== b.executedAtMs) return b.executedAtMs - a.executedAtMs;
      // US-only refinement: two US fills in the same millisecond keep their
      // exact venue order. Kalshi and Poly ordering is unchanged.
      if (a.venue === 'polymarket_us' && b.venue === 'polymarket_us') {
        const ea = exactTimeKey(a.executedAt);
        const eb = exactTimeKey(b.executedAt);
        if (ea !== eb) return ea < eb ? 1 : -1;
      }
      // Equal instants are common (a single order filling in one sweep), so
      // the tie-break has to be stable and independent of input order or the
      // rail would reshuffle on every poll.
      return a.key < b.key ? 1 : a.key > b.key ? -1 : 0;
    })
    .slice(0, limit);
}
